/**
 * Centralized icon barrel file.
 * Re-exports HugeIcons Pro (stroke-rounded) as thin wrapper components
 * that match the former lucide-react API (className, size, etc.).
 */

import { HugeiconsIcon, type HugeiconsIconProps } from '@hugeicons/react';
import {
  Activity01Icon,
  Add01Icon,
  AddCircleIcon,
  Alert01Icon,
  AlertCircleIcon,
  Analytics01Icon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  ArrowUpRight01Icon,
  Briefcase01Icon,
  Building01Icon,
  Building03Icon,
  Calendar01Icon,
  CalendarSetting01Icon,
  CallIcon,
  Cancel01Icon,
  CancelCircleIcon,
  ChartDecreaseIcon,
  ChartIncreaseIcon,
  CheckListIcon,
  CheckmarkCircle01Icon,
  CircleIcon,
  ClipboardIcon,
  Clock01Icon,
  ColorsIcon,
  ComputerIcon,
  ContainerTruckIcon,
  Copy01Icon,
  CreditCardIcon,
  DashboardBrowsingIcon,
  DatabaseIcon,
  Delete01Icon,
  Dollar01Icon,
  Download01Icon,
  Drag01Icon,
  FaceIdIcon,
  File01Icon,
  FileUnknownIcon,
  FilterIcon,
  FitToScreenIcon,
  FlashIcon,
  GlobeIcon,
  HeadphonesIcon,
  Home01Icon,
  IdentityCardIcon,
  Image01Icon,
  ImageNotFound01Icon,
  InboxIcon,
  InformationCircleIcon,
  Key01Icon,
  LanguageCircleIcon,
  LaptopIcon,
  Layers01Icon,
  Link01Icon,
  LinkSquare01Icon,
  Loading01Icon,
  Location01Icon,
  LockIcon,
  Logout01Icon,
  Mail01Icon,
  Menu01Icon,
  Message01Icon,
  Money01Icon,
  MoonIcon,
  MoreHorizontalIcon,
  NoInternetIcon,
  Notification01Icon,
  NotificationOff01Icon,
  PackageIcon,
  PartyIcon,
  PencilEdit01Icon,
  PrinterIcon,
  PercentIcon,
  Plug01Icon,
  PieChartIcon,
  PlayIcon,
  ReceiptDollarIcon,
  RecordIcon,
  RefreshIcon,
  RotateClockwiseIcon,
  RotateLeft01Icon,
  Search01Icon,
  SearchAddIcon,
  SearchMinusIcon,
  SecurityCheckIcon,
  SentIcon,
  Settings01Icon,
  Shield01Icon,
  ShieldEnergyIcon,
  ShoppingBag01Icon,
  SidebarLeftIcon,
  SlidersHorizontalIcon,
  SmartPhone01Icon,
  SquareUnlock01Icon,
  StarIcon,
  Sun01Icon,
  Tablet01Icon,
  TaskDone01Icon,
  TextNumberSignIcon,
  Tick01Icon,
  TickDouble01Icon,
  Timer01Icon,
  ToggleOffIcon,
  TractorIcon,
  UnavailableIcon,
  Undo02Icon,
  UnfoldMoreIcon,
  Upload01Icon,
  UserAdd01Icon,
  UserCheck01Icon,
  UserGroupIcon,
  UserIcon,
  UserRemove01Icon,
  UserSearch01Icon,
  ViewIcon,
  ViewOffIcon,
  Wallet01Icon,
  WarehouseIcon,
  Wifi01Icon,
  WifiDisconnected01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { type ComponentType, type SVGAttributes } from 'react';

// ---------------------------------------------------------------------------
// Icon wrapper factory
// ---------------------------------------------------------------------------

/** Props accepted by every re-exported icon – mirrors lucide-react surface. */
export type IconProps = Omit<HugeiconsIconProps, 'icon'> &
  SVGAttributes<SVGSVGElement>;

type HugeIconData = Parameters<typeof HugeiconsIcon>[0]['icon'];

function createIcon(
  icon: HugeIconData,
  displayName: string
): ComponentType<IconProps> {
  const Wrapper = (props: IconProps) => (
    <HugeiconsIcon icon={icon} {...props} />
  );
  Wrapper.displayName = displayName;
  return Wrapper;
}

// ---------------------------------------------------------------------------
// Named exports – keep alphabetical, names match former lucide-react exports
// ---------------------------------------------------------------------------

// Navigation
export const Home = createIcon(Home01Icon, 'Home');
export const ChevronDown = createIcon(ArrowDown01Icon, 'ChevronDown');
export const ChevronUp = createIcon(ArrowUp01Icon, 'ChevronUp');
export const ChevronLeft = createIcon(ArrowLeft01Icon, 'ChevronLeft');
export const ChevronRight = createIcon(ArrowRight01Icon, 'ChevronRight');
export const ChevronsUpDown = createIcon(UnfoldMoreIcon, 'ChevronsUpDown');
export const ArrowLeft = createIcon(ArrowLeft01Icon, 'ArrowLeft');
export const ArrowRight = createIcon(ArrowRight01Icon, 'ArrowRight');
export const ArrowUpRight = createIcon(ArrowUpRight01Icon, 'ArrowUpRight');
export const ArrowDown = createIcon(ArrowDown01Icon, 'ArrowDown');
export const ArrowUp = createIcon(ArrowUp01Icon, 'ArrowUp');
export const Menu = createIcon(Menu01Icon, 'Menu');
export const PanelLeft = createIcon(SidebarLeftIcon, 'PanelLeft');
export const MoreHorizontal = createIcon(MoreHorizontalIcon, 'MoreHorizontal');

// Actions
export const Search = createIcon(Search01Icon, 'Search');
export const Plus = createIcon(Add01Icon, 'Plus');
export const PlusCircle = createIcon(AddCircleIcon, 'PlusCircle');
export const X = createIcon(Cancel01Icon, 'X');
export const Check = createIcon(Tick01Icon, 'Check');
export const CheckCheck = createIcon(TickDouble01Icon, 'CheckCheck');
export const Pencil = createIcon(PencilEdit01Icon, 'Pencil');
export const Edit3 = createIcon(PencilEdit01Icon, 'Edit3');
export const Trash2 = createIcon(Delete01Icon, 'Trash2');
export const Copy = createIcon(Copy01Icon, 'Copy');
export const RefreshCw = createIcon(RefreshIcon, 'RefreshCw');
export const RotateCw = createIcon(RotateClockwiseIcon, 'RotateCw');
export const RotateCcw = createIcon(RotateLeft01Icon, 'RotateCcw');
export const GripVertical = createIcon(Drag01Icon, 'GripVertical');
export const Filter = createIcon(FilterIcon, 'Filter');
export const Send = createIcon(SentIcon, 'Send');
export const Upload = createIcon(Upload01Icon, 'Upload');
export const Download = createIcon(Download01Icon, 'Download');

// Status / Feedback
export const Loader2 = createIcon(Loading01Icon, 'Loader2');
export const AlertCircle = createIcon(AlertCircleIcon, 'AlertCircle');
export const AlertTriangle = createIcon(Alert01Icon, 'AlertTriangle');
export const CheckCircle = createIcon(CheckmarkCircle01Icon, 'CheckCircle');
export const CheckCircle2 = createIcon(CheckmarkCircle01Icon, 'CheckCircle2');
export const XCircle = createIcon(CancelCircleIcon, 'XCircle');
export const Info = createIcon(InformationCircleIcon, 'Info');
export const Ban = createIcon(UnavailableIcon, 'Ban');

// Visibility / Security
export const Eye = createIcon(ViewIcon, 'Eye');
export const EyeOff = createIcon(ViewOffIcon, 'EyeOff');
export const Lock = createIcon(LockIcon, 'Lock');
export const Unlock = createIcon(SquareUnlock01Icon, 'Unlock');
export const Shield = createIcon(Shield01Icon, 'Shield');
export const ShieldCheck = createIcon(SecurityCheckIcon, 'ShieldCheck');
export const ShieldAlert = createIcon(ShieldEnergyIcon, 'ShieldAlert');
export const Bell = createIcon(Notification01Icon, 'Bell');
export const BellOff = createIcon(NotificationOff01Icon, 'BellOff');

// Content / Time
export const FileText = createIcon(File01Icon, 'FileText');
export const FileQuestion = createIcon(FileUnknownIcon, 'FileQuestion');
export const Calendar = createIcon(Calendar01Icon, 'Calendar');
export const CalendarClock = createIcon(CalendarSetting01Icon, 'CalendarClock');
export const Clock = createIcon(Clock01Icon, 'Clock');
export const Timer = createIcon(Timer01Icon, 'Timer');

// Finance
export const DollarSign = createIcon(Dollar01Icon, 'DollarSign');
export const CreditCard = createIcon(CreditCardIcon, 'CreditCard');
export const Banknote = createIcon(Money01Icon, 'Banknote');
export const Percent = createIcon(PercentIcon, 'Percent');
export const Wallet = createIcon(Wallet01Icon, 'Wallet');
export const Receipt = createIcon(ReceiptDollarIcon, 'Receipt');
export const Briefcase = createIcon(Briefcase01Icon, 'Briefcase');

// People
export const User = createIcon(UserIcon, 'User');
export const Users = createIcon(UserGroupIcon, 'Users');
export const UserPlus = createIcon(UserAdd01Icon, 'UserPlus');
export const UserMinus = createIcon(UserRemove01Icon, 'UserMinus');
export const UserCheck = createIcon(UserCheck01Icon, 'UserCheck');
export const UserSearch = createIcon(UserSearch01Icon, 'UserSearch');

// Places
export const Building = createIcon(Building01Icon, 'Building');
export const Building2 = createIcon(Building03Icon, 'Building2');
export const MapPin = createIcon(Location01Icon, 'MapPin');
export const Globe = createIcon(GlobeIcon, 'Globe');

// Devices
export const Smartphone = createIcon(SmartPhone01Icon, 'Smartphone');
export const Laptop = createIcon(LaptopIcon, 'Laptop');
export const Tablet = createIcon(Tablet01Icon, 'Tablet');
export const Headphones = createIcon(HeadphonesIcon, 'Headphones');
export const Phone = createIcon(CallIcon, 'Phone');

// Communication
export const Mail = createIcon(Mail01Icon, 'Mail');
export const MessageSquare = createIcon(Message01Icon, 'MessageSquare');

// Data / Charts
export const BarChart3 = createIcon(Analytics01Icon, 'BarChart3');
export const PieChart = createIcon(PieChartIcon, 'PieChart');
export const TrendingUp = createIcon(ChartIncreaseIcon, 'TrendingUp');
export const TrendingDown = createIcon(ChartDecreaseIcon, 'TrendingDown');
export const Activity = createIcon(Activity01Icon, 'Activity');
export const Database = createIcon(DatabaseIcon, 'Database');
export const DatabaseZap = createIcon(DatabaseIcon, 'DatabaseZap');

// Layout
export const LayoutDashboard = createIcon(
  DashboardBrowsingIcon,
  'LayoutDashboard'
);
export const ClipboardList = createIcon(ClipboardIcon, 'ClipboardList');
export const ClipboardCheck = createIcon(TaskDone01Icon, 'ClipboardCheck');
export const ListChecks = createIcon(CheckListIcon, 'ListChecks');

// Commerce / Inventory
export const Package = createIcon(PackageIcon, 'Package');
export const Box = createIcon(PackageIcon, 'Box');
export const Warehouse = createIcon(WarehouseIcon, 'Warehouse');
export const Truck = createIcon(ContainerTruckIcon, 'Truck');
export const Layers = createIcon(Layers01Icon, 'Layers');
export const ShoppingBag = createIcon(ShoppingBag01Icon, 'ShoppingBag');
export const Tractor = createIcon(TractorIcon, 'Tractor');

// Settings / Misc
export const Settings = createIcon(Settings01Icon, 'Settings');
export const Star = createIcon(StarIcon, 'Star');
export const Sun = createIcon(Sun01Icon, 'Sun');
export const Moon = createIcon(MoonIcon, 'Moon');
export const Monitor = createIcon(ComputerIcon, 'Monitor');
export const Zap = createIcon(FlashIcon, 'Zap');
export const Plug = createIcon(Plug01Icon, 'Plug');
export const Play = createIcon(PlayIcon, 'Play');
export const Circle = createIcon(CircleIcon, 'Circle');
export const Dot = createIcon(RecordIcon, 'Dot');

// Auth
export const LogOut = createIcon(Logout01Icon, 'LogOut');
export const Languages = createIcon(LanguageCircleIcon, 'Languages');
export const KeyRound = createIcon(Key01Icon, 'KeyRound');

// Links / Text
export const Link = createIcon(Link01Icon, 'Link');
export const ExternalLink = createIcon(LinkSquare01Icon, 'ExternalLink');
export const Printer = createIcon(PrinterIcon, 'Printer');
export const Hash = createIcon(TextNumberSignIcon, 'Hash');

// Images
export const ImageIcon = createIcon(Image01Icon, 'ImageIcon');
export const ImageOff = createIcon(ImageNotFound01Icon, 'ImageOff');
export const FaceId = createIcon(FaceIdIcon, 'FaceId');
export const IdCard = createIcon(IdentityCardIcon, 'IdCard');

// Connectivity
export const Wifi = createIcon(Wifi01Icon, 'Wifi');
export const WifiOff = createIcon(WifiDisconnected01Icon, 'WifiOff');
export const CloudOff = createIcon(NoInternetIcon, 'CloudOff');

// Controls
export const Sliders = createIcon(SlidersHorizontalIcon, 'Sliders');
export const Palette = createIcon(ColorsIcon, 'Palette');
export const ToggleLeft = createIcon(ToggleOffIcon, 'ToggleLeft');
export const ZoomIn = createIcon(SearchAddIcon, 'ZoomIn');
export const ZoomOut = createIcon(SearchMinusIcon, 'ZoomOut');
export const FitToWidth = createIcon(FitToScreenIcon, 'FitToWidth');
export const Undo2 = createIcon(Undo02Icon, 'Undo2');
export const Inbox = createIcon(InboxIcon, 'Inbox');

// Celebrations
export const PartyPopper = createIcon(PartyIcon, 'PartyPopper');

// People (alias)
export { User as UserIcon };

// Misc re-exports for aliased imports
export const LinkIcon = Link;

// Re-export the underlying HugeiconsIcon for direct usage if needed
export { HugeiconsIcon } from '@hugeicons/react';
export type { HugeiconsIconProps } from '@hugeicons/react';
